import {
    HubPersona,
    PersonType,
    Person,
    UnifiedGroup,
    uniqBy,
    normalizeRelevancyScore,
    INVALID_SEARCH_RELEVANCY_SCORE,
    DEFAULT_SEARCH_RELEVANCY_SCORE,
} from '../../shared';
import type { HubPersonasMap, UnifiedGroupsMap } from '../../appstate';

const findMatchingContactForGalId = (
    recipientCacheResult: Person,
    personas: HubPersona[]
): HubPersona => {
    return (personas as Person[]).find(
        persona =>
            persona.type === PersonType.Person &&
            persona.hubPersonaId !== recipientCacheResult.hubPersonaId &&
            persona.info.GALContactGuid === recipientCacheResult.info.GALContactGuid
    );
};

const findMatchingContactForRecipientCacheResult = (
    recipientCacheResult: HubPersona,
    personas: HubPersona[],
    knownGroups: UnifiedGroup[]
) => {
    let match: HubPersona = recipientCacheResult;
    if (recipientCacheResult) {
        if (recipientCacheResult.type === PersonType.GroupBasicData) {
            const directoryId = recipientCacheResult.externalDirectoryObjectId;
            const hubPersonaId = recipientCacheResult.hubPersonaId;
            match =
                knownGroups.find(
                    group =>
                        group.type === PersonType.Group &&
                        ((!!directoryId && group.externalDirectoryObjectId === directoryId) ||
                            group.hubPersonaId === hubPersonaId)
                ) || match;
        }

        if (recipientCacheResult.type === PersonType.Person) {
            if (!!recipientCacheResult.info.GALContactGuid) {
                match = findMatchingContactForGalId(recipientCacheResult, personas) || match;
            }
        }
    }

    if (match) {
        return {
            ...match,
            relevancyScore: recipientCacheResult.relevancyScore,
        };
    }

    return match;
};

// If we encounter a backend result with "Invalid" relevancy, fallback to the preceding or following result's relevancy if possible
const getValidRelevance = (index: number, backendResults: HubPersona[]): number => {
    const result = backendResults[index];
    if (result.relevancyScore !== INVALID_SEARCH_RELEVANCY_SCORE) {
        return result.relevancyScore;
    }

    // For all but the first result can use the previous valid result's relevancy
    if (index > 0) {
        for (let i = index - 1; i > 0; i--) {
            const relevance = backendResults[i].relevancyScore;
            if (relevance !== INVALID_SEARCH_RELEVANCY_SCORE) {
                return relevance;
            }
        }
    }

    // If we didn't find a valid result, or were at the start, walk forwards instead
    if (index < backendResults.length) {
        for (let i = index + 1; i < backendResults.length; i++) {
            const relevance = backendResults[i].relevancyScore;
            if (relevance !== INVALID_SEARCH_RELEVANCY_SCORE) {
                return relevance;
            }
        }
    }

    // We were the only result, or all results were invalid (relevancy isn't important in this case anyway)
    return DEFAULT_SEARCH_RELEVANCY_SCORE;
};

export const mergeAndExpandResults = (
    hubPersonasMap: HubPersonasMap,
    groupsMap: UnifiedGroupsMap,
    backendResultsForQuery: string[],
    backendResultsAreComplete: boolean,
    localResultsForQuery: HubPersona[]
) => {
    const knownPersonas = Object.keys(hubPersonasMap).map(key => hubPersonasMap[key]);
    const knownGroups = Object.keys(groupsMap).map(key => groupsMap[key]);

    const resolvedBackendResults = (backendResultsForQuery || [])
        .filter(hubPersonaId => hubPersonasMap[hubPersonaId]) // Check if hubPersonaId still exists
        .map(hubPersonaId => {
            const backendMatch = hubPersonasMap[hubPersonaId] || groupsMap[hubPersonaId];
            return findMatchingContactForRecipientCacheResult(
                backendMatch,
                knownPersonas,
                knownGroups
            );
        })
        .filter(result => !!result);

    const relevancyFixedBackendResults = resolvedBackendResults
        // Subtract ε < 1 (index / count) to tie-break equal relevancy with order returned, ceiling to remove previous iterations
        .map((hubPerson, index) => ({
            ...hubPerson,
            relevancyScore:
                normalizeRelevancyScore(
                    Math.ceil(getValidRelevance(index, resolvedBackendResults))
                ) -
                index / resolvedBackendResults.length,
        }));

    const localResultsWithValidRelevance = (localResultsForQuery || []).map(hubPerson => ({
        ...hubPerson,
        relevancyScore: normalizeRelevancyScore(hubPerson.relevancyScore),
    }));

    // Skew relevancy of all local results so that they are less relevant than the last federated result (with a buffer of 1)
    const maxLocalRelevancy = Math.max(
        ...(localResultsWithValidRelevance || []).map(hubPersona => hubPersona.relevancyScore)
    );
    const minBackendRelevancy = Math.min(
        ...relevancyFixedBackendResults.map(hubPersona => hubPersona.relevancyScore)
    );
    const buffer = 1;
    const relevancyShift = Math.min(0, minBackendRelevancy - (maxLocalRelevancy + buffer));

    return uniqBy<HubPersona>(
        [
            // Display backend results by descending relevance
            ...relevancyFixedBackendResults,
            // Backfill with local results only when recipient cache has loaded
            ...(backendResultsAreComplete
                ? localResultsWithValidRelevance.map(hubPersona => ({
                      ...hubPersona,
                      relevancyScore: hubPersona.relevancyScore + relevancyShift,
                  }))
                : []),
        ],
        // Filter out showing both contact and directory entries for the same person, if they are sharing a GALContactGuid
        // same logic applies to groups if they share an email address
        hubPersona =>
            (hubPersona.type === PersonType.Person && hubPersona.info.GALContactGuid) ||
            hubPersona.hubPersonaId
    );
};
